import React, { useCallback } from 'react';

import { useAppMonitor } from '../../../../monitor';
import { showNotOnMobileDialog } from '../../../../store/actions/popupActions';
import {
  getInsertableObjectHotCharacter,
  getInsertableObjects,
  InsertableObject,
} from '../../../../store/selectors/insertableObjectsSelector';
import { useAppDispatch, useAppSelector, useIsClientInMobileMode } from '../../../hooks';

export function useInsertableObjectsMenuInternals(monitorText: string, onChooseItem: () => void) {
  const monitor = useAppMonitor();
  const dispatch = useAppDispatch();

  const mobile = useIsClientInMobileMode();

  const insertableObjects = useAppSelector(getInsertableObjects);

  const insert = useCallback(
    (obj: InsertableObject) => {
      if (mobile) {
        onChooseItem();
        dispatch(showNotOnMobileDialog('Creating items is not yet available on mobile.'));
        return;
      }

      obj.trigger(dispatch);
      monitor.recordInteraction(`${monitorText} => Insert ${obj.name} clicked`);
      onChooseItem();
    },
    [dispatch, mobile, monitor, monitorText, onChooseItem],
  );

  const handleHotCharacterKeyDown = useCallback(
    (event: KeyboardEvent | React.KeyboardEvent<HTMLElement>) => {
      if (event.code === 'Escape') {
        event.preventDefault();
        onChooseItem();
        return;
      }

      const matchingObject = insertableObjects.find((o) => {
        const hot = getInsertableObjectHotCharacter(o);
        return hot != null && hot === event.key;
      });

      if (matchingObject && !matchingObject.disabled) {
        event.preventDefault();
        insert(matchingObject);
      }
    },
    [insert, insertableObjects, onChooseItem],
  );

  return { insertableObjects, insert, handleHotCharacterKeyDown };
}
